import LanguageButton from '@/components/common/LanguageButton';
import Modal from '@/components/modal/Modal';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import React, { CSSProperties, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DesktopStepPosition = 'top' | 'right' | 'bottom' | 'left' | 'center';
export type HighlightStyle = 'pulse' | 'arrows' | 'sweep';

interface StepLayoutProps {
    children: ReactNode;
    title: string;
    description?: string;
    isActive: boolean;
    targetSelector?: string | string[];
    desktopPosition?: DesktopStepPosition;
    horizontalPadding?: number;
    currentStep?: number;
    totalSteps?: number;
}

const DESKTOP_PANEL_WIDTH = 480;
const VIEWPORT_MARGIN = 32;
const TARGET_HIGHLIGHT_HORIZONTAL_PADDING = 6;
const TARGET_HIGHLIGHT_VERTICAL_PADDING = 1.5;
const PANEL_GAP = 28;
const MIN_PANEL_HEIGHT = 200;
const highlightStyle = 'pulse';

const StepLayout: React.FC<StepLayoutProps> = ({
    children,
    title,
    description,
    isActive,
    targetSelector,
    desktopPosition = 'center',
    horizontalPadding
}) => {
    const modalHandlers = {
        open: () => {},
        close: () => {},
        toggle: () => {}
    };
    const panelRef = useRef<HTMLDivElement>(null);
    const spotlightRef = useRef<SVGSVGElement>(null);
    const { mobile, tablet } = useScreenSizes();
    const isCompactScreen = useMemo(() => mobile || tablet, [mobile, tablet]);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

    const horizontalPaddingValue = useMemo(
        () => horizontalPadding ?? TARGET_HIGHLIGHT_HORIZONTAL_PADDING,
        [horizontalPadding]
    );

    // Improved positioning algorithm with fallback positions
    const calculateOptimalPosition = useCallback(
        (
            targetRect: DOMRect,
            panelWidth: number,
            panelHeight: number,
            preferredPosition: DesktopStepPosition
        ): { top: number; left: number; position: DesktopStepPosition } => {
            const viewport = {
                width: window.innerWidth,
                height: window.innerHeight,
                top: VIEWPORT_MARGIN,
                bottom: window.innerHeight - VIEWPORT_MARGIN,
                left: VIEWPORT_MARGIN,
                right: window.innerWidth - VIEWPORT_MARGIN
            };

            // Calculate available space in each direction
            const spaces = {
                top: targetRect.top - viewport.top,
                bottom: viewport.bottom - targetRect.bottom,
                left: targetRect.left - viewport.left,
                right: viewport.right - targetRect.right
            };

            // Try positions in order: preferred first, then alternatives based on available space
            const positionCandidates: DesktopStepPosition[] = [preferredPosition];

            if (preferredPosition !== 'center') {
                // Add alternatives sorted by available space
                const sorted = Object.entries(spaces)
                    .sort(([, a], [, b]) => b - a)
                    .map(([pos]) => pos as DesktopStepPosition);

                sorted.forEach((pos) => {
                    if (pos !== preferredPosition && !positionCandidates.includes(pos)) {
                        positionCandidates.push(pos);
                    }
                });
            }

            for (const position of positionCandidates) {
                let top = viewport.top;
                let left = viewport.left;
                let fits = true;

                switch (position) {
                    case 'top':
                        top = targetRect.top - panelHeight - PANEL_GAP;
                        left = targetRect.left + targetRect.width / 2 - panelWidth / 2;
                        fits = spaces.top >= panelHeight + PANEL_GAP;
                        break;
                    case 'bottom':
                        top = targetRect.bottom + PANEL_GAP;
                        left = targetRect.left + targetRect.width / 2 - panelWidth / 2;
                        fits = spaces.bottom >= panelHeight + PANEL_GAP;
                        break;
                    case 'left':
                        top = targetRect.top + targetRect.height / 2 - panelHeight / 2;
                        left = targetRect.left - panelWidth - PANEL_GAP;
                        fits = spaces.left >= panelWidth + PANEL_GAP;
                        break;
                    case 'right':
                        top = targetRect.top + targetRect.height / 2 - panelHeight / 2;
                        left = targetRect.right + PANEL_GAP;
                        fits = spaces.right >= panelWidth + PANEL_GAP;
                        break;
                    case 'center':
                    default:
                        top = viewport.top + (viewport.bottom - viewport.top - panelHeight) / 2;
                        left = viewport.left + (viewport.right - viewport.left - panelWidth) / 2;
                        fits = true;
                        break;
                }

                if (fits) {
                    // Ensure panel stays within viewport
                    top = Math.max(viewport.top, Math.min(top, viewport.bottom - panelHeight));
                    left = Math.max(viewport.left, Math.min(left, viewport.right - panelWidth));

                    return { top, left, position };
                }
            }

            // Fallback: center position with forced fit
            const topPos = Math.max(
                viewport.top,
                Math.min(
                    viewport.top + (viewport.bottom - viewport.top - panelHeight) / 2,
                    viewport.bottom - panelHeight
                )
            );
            const leftPos = Math.max(
                viewport.left,
                Math.min(viewport.left + (viewport.right - viewport.left - panelWidth) / 2, viewport.right - panelWidth)
            );

            return { top: topPos, left: leftPos, position: 'center' };
        },
        []
    );

    useEffect(() => {
        if (!isActive || isCompactScreen || !targetSelector) {
            setTargetRect(null);
            setPanelStyle({});
            return;
        }

        const selectors = Array.isArray(targetSelector) ? targetSelector : [targetSelector];

        const calculateDesktopLayout = () => {
            const targets = selectors
                .map((selector) => document.querySelector(selector) as HTMLElement | null)
                .filter((element): element is HTMLElement => Boolean(element));

            if (!targets.length) {
                setTargetRect(null);
                setPanelStyle({});
                return;
            }

            const rects = targets.map((target) => target.getBoundingClientRect());
            const unionRect = rects.reduce((combined, rect) => {
                const left = Math.min(combined.left, rect.left);
                const top = Math.min(combined.top, rect.top);
                const right = Math.max(combined.right, rect.right);
                const bottom = Math.max(combined.bottom, rect.bottom);

                return new DOMRect(left, top, right - left, bottom - top);
            }, rects[0]);

            setTargetRect((currentRect) => {
                if (
                    currentRect &&
                    currentRect.top === unionRect.top &&
                    currentRect.left === unionRect.left &&
                    currentRect.width === unionRect.width &&
                    currentRect.height === unionRect.height
                ) {
                    return currentRect;
                }

                return unionRect;
            });
            const panelWidth = Math.min(DESKTOP_PANEL_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
            const panelHeight = Math.max(panelRef.current?.offsetHeight ?? MIN_PANEL_HEIGHT, MIN_PANEL_HEIGHT);

            const { top, left } = calculateOptimalPosition(unionRect, panelWidth, panelHeight, desktopPosition);

            setPanelStyle({
                width: `${panelWidth}px`,
                top: `${Math.round(top)}px`,
                left: `${Math.round(left)}px`
            });
        };

        // Use RAF for smooth updates
        let animationFrameId: number;
        const scheduleLayout = () => {
            animationFrameId = window.requestAnimationFrame(() => {
                calculateDesktopLayout();
                scheduleLayout();
            });
        };

        scheduleLayout();

        const handleResize = () => calculateDesktopLayout();
        const handleScroll = () => calculateDesktopLayout();

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [desktopPosition, isActive, isCompactScreen, targetSelector]);

    return (
        <Fragment>
            {!isCompactScreen && targetRect && targetSelector ? (
                <Fragment>
                    {/* Spotlight overlay */}
                    <svg
                        ref={spotlightRef}
                        className="fixed inset-0 z-50 pointer-events-none"
                        style={{ width: '100%', height: '100%' }}>
                        <defs>
                            <mask id="spotlight-mask">
                                <rect width="100%" height="100%" fill="white" />
                                <rect
                                    x={targetRect.left - horizontalPaddingValue}
                                    y={targetRect.top - TARGET_HIGHLIGHT_VERTICAL_PADDING}
                                    width={targetRect.width + horizontalPaddingValue * 2}
                                    height={targetRect.height + TARGET_HIGHLIGHT_VERTICAL_PADDING * 2}
                                    fill="black"
                                    ry="6"
                                    rx="6"
                                />
                            </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.6)" mask="url(#spotlight-mask)" />
                    </svg>

                    {/* Target highlight border */}
                    <div
                        className={cn('fixed z-50 pointer-events-none')}
                        style={{
                            top: `${targetRect.top - TARGET_HIGHLIGHT_VERTICAL_PADDING}px`,
                            left: `${targetRect.left - horizontalPaddingValue}px`,
                            width: `${targetRect.width + horizontalPaddingValue * 2}px`,
                            height: `${targetRect.height + TARGET_HIGHLIGHT_VERTICAL_PADDING * 2}px`
                        }}
                    />
                    {highlightStyle === 'pulse' && (
                        <div
                            className="tour-highlight-pulse-ring fixed z-50 pointer-events-none"
                            style={{
                                top: `${targetRect.top - TARGET_HIGHLIGHT_VERTICAL_PADDING}px`,
                                left: `${targetRect.left - horizontalPaddingValue}px`,
                                width: `${targetRect.width + horizontalPaddingValue * 2}px`,
                                height: `${targetRect.height + TARGET_HIGHLIGHT_VERTICAL_PADDING * 2}px`
                            }}
                        />
                    )}
                    <div
                        ref={panelRef}
                        className="fixed z-[100] max-h-[calc(100vh-48px)] overflow-y-auto rounded-md border border-brand-neutral-600 bg-brand-background shadow-2xl"
                        style={panelStyle}>
                        <div className="space-y-2 p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 pr-6">
                                    <div className="text-xl font-bold text-brand-text">{title}</div>
                                </div>
                                <LanguageButton hasBorders={false} direction="left" />
                            </div>
                            {description && <p className="text-sm text-brand-neutral-400 mb-2">{description}</p>}
                            <div>{children}</div>
                        </div>
                    </div>
                </Fragment>
            ) : (
                <Modal
                    opened={isActive}
                    handlers={modalHandlers}
                    size="max-w-2xl"
                    className="h-auto border border-brand-neutral-600 animate-fadeIn">
                    <div className="space-y-4 p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 pr-6">
                                <div className="mb-2 text-xl font-bold text-brand-text capitalize">{title}</div>
                                {description && <p className="text-sm text-brand-neutral-400">{description}</p>}
                            </div>
                            <LanguageButton hasBorders={false} direction="left" />
                        </div>
                        <div>{children}</div>
                    </div>
                </Modal>
            )}
        </Fragment>
    );
};

export default StepLayout;
